import PropTypes from "prop-types";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { Dropdown, DropdownToggle, DropdownMenu, Button } from "reactstrap";
import { withTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

import logo from "../../assets/images/logo.svg";
import NotificationModal from "pages/AllCommonModal/NotificationModal/notification-modal";
import { useNotificationContext } from "libs/context/NotificationContext";

import {
  showRightSidebarAction,
  toggleLeftmenu,
  changeSidebarType,
} from "../../store/actions";
import MessageModal from "pages/AllCommonModal/MesssageModal/message-modal";
import HorizontalAppMenu from "./HorizontalAppMenu";
import SimpleNotificationModal from "pages/AllCommonModal/NotificationModal/simple-notification-modal";
import BreakTimerModal from "pages/AllCommonModal/BreakTimerModal/break-timer-modal";
import ProfileModal from "pages/AllCommonModal/ProfileModal/profile-modal";
import HeaderGlobalSearch from "components/Common/HeaderGlobalSearch";
import {
  getBreakElapsedSeconds,
  getBreakElapsedSecondsFromClock,
  parseServerTimeIso,
  registerServerTimeHandler,
} from "libs/context/serverTimeBridge";
import { endSessionBreak, startSessionBreak } from "model/SessionModel";

const BREAK_START_ISO_STORAGE_KEY = "breakStartIso";
const BREAK_SYNC_CHANNEL = "session-break-sync";

const readStoredBreakStartIso = () => {
  try {
    const stored = sessionStorage.getItem(BREAK_START_ISO_STORAGE_KEY);
    return stored ? String(stored) : null;
  } catch {
    return null;
  }
};

const writeStoredBreakStartIso = (breakStartIso) => {
  try {
    const value = String(breakStartIso ?? "").trim();
    if (value) {
      sessionStorage.setItem(BREAK_START_ISO_STORAGE_KEY, value);
      return;
    }
    sessionStorage.removeItem(BREAK_START_ISO_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
};

const Header = (props) => {
  const {
    changeSidebarType,
    leftSideBarType,
    menuLayoutMode = "vertical",
    onMenuLayoutChange,
  } = props;
  const [logoDropdown, setLogoDropdown] = useState(false);
  const [logoutDropdown, setLogoutDropdown] = useState(false);
  const [dateTime, setDateTime] = useState(new Date());
  const [notificationModalOpenClose, setNotificationModalOpenClose] =
    useState(false);

  const [messageModalOpenClose, setMessageModalOpenClose] =
    useState(false);
  const [breakModalOpen, setBreakModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [breakSeconds, setBreakSeconds] = useState(0);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [isStartingBreak, setIsStartingBreak] = useState(false);
  const [isResumingBreak, setIsResumingBreak] = useState(false);

  const notificationRef = useRef(null);
  const messageRef = useRef(null);
  const serverTimeOffsetRef = useRef(null);
  const breakStartIsoRef = useRef(readStoredBreakStartIso());
  const previousBreakSecondsRef = useRef(0);
  const onBreakRef = useRef(false);
  const breakChannelRef = useRef(null);
  const { unreadCount } = useNotificationContext() || {};

  useEffect(() => {
    breakStartIsoRef.current = readStoredBreakStartIso();
  }, []);


  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const getCurrentServerTime = useCallback(() => {
    if (serverTimeOffsetRef.current !== null) {
      return new Date(Date.now() + serverTimeOffsetRef.current);
    }
    return new Date();
  }, []);

  const updateBreakSeconds = useCallback(() => {
    if (!onBreakRef.current || !breakStartIsoRef.current) return;

    setBreakSeconds(
      getBreakElapsedSeconds(
        breakStartIsoRef.current,
        getCurrentServerTime(),
        previousBreakSecondsRef.current
      )
    );
  }, [getCurrentServerTime]);

  const syncBreakFromClock = useCallback((clock, serverDate) => {
    previousBreakSecondsRef.current = Math.max(
      0,
      Number(clock?.breakTimeSeconds) || 0
    );

    if (clock?.breakStartIso) {
      breakStartIsoRef.current = clock.breakStartIso;
      writeStoredBreakStartIso(clock.breakStartIso);
    }

    if (!breakStartIsoRef.current) return;

    setBreakSeconds(
      getBreakElapsedSecondsFromClock(
        clock,
        serverDate ?? getCurrentServerTime()
      )
    );
  }, [getCurrentServerTime]);

  const applyServerClock = useCallback((clock) => {
    const parsed = parseServerTimeIso(clock?.serverTimeIso);
    if (parsed) {
      serverTimeOffsetRef.current = parsed.getTime() - Date.now();
      setDateTime(parsed);
    }

    const nextOnBreak = Boolean(clock?.onBreak);
    onBreakRef.current = nextOnBreak;
    setIsOnBreak(nextOnBreak);

    if (nextOnBreak) {
      syncBreakFromClock(clock, parsed);
      setBreakModalOpen(true);
    } else {
      breakStartIsoRef.current = null;
      previousBreakSecondsRef.current = 0;
      writeStoredBreakStartIso(null);
      setBreakModalOpen(false);
      setBreakSeconds(0);
    }
  }, [syncBreakFromClock]);

  /* Broadcast break changes to other open tabs in the same browser so they
     react instantly instead of waiting for the next SignalR clock push. */
  const broadcastBreakState = useCallback((clock) => {
    try {
      breakChannelRef.current?.postMessage(clock);
    } catch {
      // ignore broadcast errors
    }
  }, []);

  useEffect(() => {
    registerServerTimeHandler(applyServerClock);
    return () => registerServerTimeHandler(null);
  }, [applyServerClock]);

  /* Cross-tab break sync: when one tab starts/ends a break, every other open
     tab applies the same break state immediately. */
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined;

    const channel = new BroadcastChannel(BREAK_SYNC_CHANNEL);
    breakChannelRef.current = channel;

    channel.onmessage = (event) => {
      const clock = event?.data;
      if (clock && typeof clock === "object") {
        applyServerClock(clock);
      }
    };

    return () => {
      channel.close();
      breakChannelRef.current = null;
    };
  }, [applyServerClock]);

  /* LIVE DATE TIME — seeded from SignalR server clock when available */
  useEffect(() => {
    const timer = setInterval(() => {
      if (serverTimeOffsetRef.current !== null) {
        setDateTime(new Date(Date.now() + serverTimeOffsetRef.current));
      } else {
        setDateTime(new Date());
      }

      if (onBreakRef.current) {
        updateBreakSeconds();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [updateBreakSeconds]);

  const formattedDateTime = dateTime.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  const handleSidebarToggle = () => {
    if (window.innerWidth < 992) {
      document.body.classList.toggle("sidebar-enable");
      return;
    }

    document.body.classList.remove("sidebar-hidden-full");
    const nextSidebarType =
      leftSideBarType === "condensed" ? "default" : "condensed";
    changeSidebarType?.(nextSidebarType, false);
  };

  const handleBreakClick = async () => {
    if (isOnBreak || isStartingBreak) return;

    const swalResult = await Swal.fire({
      title: "Go for Break?",
      text: "Are you sure do you want to go for Break ?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (!swalResult?.isConfirmed) return;

    setIsStartingBreak(true);
    try {
      const response = await startSessionBreak();
      if (response?.success || response?.isSuccess) {
        const breakStartIso = response?.data?.breakStartIso ?? null;
        onBreakRef.current = true;
        setIsOnBreak(true);
        if (breakStartIso) {
          breakStartIsoRef.current = breakStartIso;
          writeStoredBreakStartIso(breakStartIso);
        }
        setBreakSeconds(0);
        setBreakModalOpen(true);
        broadcastBreakState({
          onBreak: true,
          breakStartIso,
          breakTimeSeconds: 0,
        });
        return;
      }
      toast.error(response?.message || "Unable to start break");
    } finally {
      setIsStartingBreak(false);
    }
  };

  const handleResumeBreak = async () => {
    if (isResumingBreak) return;

    setIsResumingBreak(true);
    try {
      const response = await endSessionBreak();
      if (response?.success || response?.isSuccess) {
        setBreakModalOpen(false);
        onBreakRef.current = false;
        setIsOnBreak(false);
        breakStartIsoRef.current = null;
        previousBreakSecondsRef.current = 0;
        writeStoredBreakStartIso(null);
        setBreakSeconds(0);
        broadcastBreakState({ onBreak: false });
        return;
      }
      toast.error(response?.message || "Unable to resume session");
    } finally {
      setIsResumingBreak(false);
    }
  };

  const renderHeaderActions = () => (
    <div className="navbar-header-actions d-flex align-items-center">
      <div className="d-flex align-items-center me-2 me-lg-3">
        <i className="fa fa-clock me-2 text-warning font-20" />
        <div className="px-2 px-lg-3 py-1 py-lg-2 fw-semibold format-date-background navbar-header-datetime">
          {formattedDateTime}
        </div>
      </div>

      <div className="me-1 me-lg-2">
        <button
          type="button"
          className="btn header-item break-icon"
          onClick={handleBreakClick}
          disabled={isStartingBreak || isOnBreak}
          title="Take a break"
          aria-label="Take a break"
        >
          {isStartingBreak ? (
            <i className="fa fa-spinner fa-spin" />
          ) : (
            <i className="fa fa-coffee" />
          )}
        </button>
      </div>

      <div ref={notificationRef} className="me-1 me-lg-2">
        <button
          className="btn header-item noti-icon position-relative"
          onClick={() => setNotificationModalOpenClose(true)}
        >
          <i className="fa fa-bell" />
          {unreadCount > 0 ? (
            <span className="badge bg-danger rounded-pill position-absolute top-1 end-0">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          ) : null}
        </button>
      </div>

      <div className="me-1 me-lg-2">
        <button
          type="button"
          className="btn header-item noti-icon"
          onClick={() => {
            props.showRightSidebarAction(!props.showRightSidebar);
          }}
          title="Settings"
          aria-label="Open settings"
        >
          <i className="bx bx-cog bx-spin" />
        </button>
      </div>

      <Dropdown
        isOpen={logoutDropdown}
        toggle={() => setLogoutDropdown(!logoutDropdown)}
      >
        <DropdownToggle className="btn header-item" tag="button">
          <i className="fa fa-user-circle font-20" />
        </DropdownToggle>
        <DropdownMenu end>
          <button
            className="dropdown-item"
            onClick={() => {
              setLogoutDropdown(false);
              setProfileModalOpen(true);
            }}
          >
            <i className="fa fa-user me-1" />
            Profile
          </button>
          <div className="dropdown-divider" />
          <button
            className="dropdown-item text-danger"
            onClick={handleLogout}
          >
            <i className="fa fa-sign-out" />
            Logout
          </button>
          <button
            className="dropdown-item text-danger"
            onClick={() => {
              window.location.reload();
            }}
          >
            <i className="fa fa-refresh" />
            Hard Refresh
          </button>
          <div className="dropdown-divider" />
          <button
            className={`dropdown-item ${
              menuLayoutMode === "vertical" ? "active" : ""
            }`}
            onClick={() =>
              typeof onMenuLayoutChange === "function" &&
              onMenuLayoutChange("vertical")
            }
          >
            <i className="fa fa-columns me-1" />
            Vertical Menu
          </button>
          <button
            className={`dropdown-item ${
              menuLayoutMode === "horizontal" ? "active" : ""
            }`}
            onClick={() =>
              typeof onMenuLayoutChange === "function" &&
              onMenuLayoutChange("horizontal")
            }
          >
            <i className="fa fa-window-maximize me-1" />
            Horizontal Menu
          </button>
        </DropdownMenu>
      </Dropdown>

      {/* <NotificationModal
        anchorRef={notificationRef}
        notificationModalOpenClose={notificationModalOpenClose}
        setNotificationModalOpenClose={setNotificationModalOpenClose}
      /> */}

      <SimpleNotificationModal
       anchorRef={notificationRef}
       notificationModalOpenClose={notificationModalOpenClose}
       setNotificationModalOpenClose={setNotificationModalOpenClose} />

      <MessageModal
        messageRef={messageRef}
        messageModalOpenClose={messageModalOpenClose}
        setMessageModalOpenClose={setMessageModalOpenClose}
      />

      <BreakTimerModal
        isOpen={breakModalOpen}
        breakSeconds={breakSeconds}
        onResume={handleResumeBreak}
        isResuming={isResumingBreak}
      />

      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />
    </div>
  );

  if (menuLayoutMode === "horizontal") {
    return (
      <header
        id="page-topbar"
        className="page-topbar--horizontal-unified"
      >
        <div className="navbar-header navbar-header--unified d-flex align-items-stretch">
          {isMobile ? (
            <Dropdown
              isOpen={logoDropdown}
              toggle={() => setLogoDropdown(!logoDropdown)}
              className="navbar-brand-box navbar-brand-box--unified flex-shrink-0"
            >
              <DropdownToggle tag="div" className="cursor-pointer">
                <img src={logo} alt="logo" height="22" />
              </DropdownToggle>
              <DropdownMenu>
                <button
                  className="dropdown-item text-danger"
                  onClick={handleLogout}
                >
                  <i className="fa fa-sign-out" />
                  Logout
                </button>
                <button
                  className="dropdown-item text-danger"
                  onClick={() => window.location.reload()}
                >
                  <i className="fa fa-refresh" />
                  Hard Refresh
                </button>
                <div className="dropdown-divider" />
                <button
                  className="dropdown-item active"
                  onClick={() =>
                    typeof onMenuLayoutChange === "function" &&
                    onMenuLayoutChange("vertical")
                  }
                >
                  <i className="fa fa-columns me-1" />
                  Vertical Menu
                </button>
                <button
                  className="dropdown-item"
                  onClick={() =>
                    typeof onMenuLayoutChange === "function" &&
                    onMenuLayoutChange("horizontal")
                  }
                >
                  <i className="fa fa-window-maximize me-1" />
                  Horizontal Menu
                </button>
              </DropdownMenu>
            </Dropdown>
          ) : null}
          <div className="navbar-header-menu flex-grow-1 min-w-0">
            <HorizontalAppMenu embedded />
          </div>
          <HeaderGlobalSearch
            className="d-none d-md-flex mx-2"
            variant="horizontal"
          />
          {renderHeaderActions()}
        </div>
      </header>
    );
  }

  return (
    <header id="page-topbar">
      <div className="navbar-header d-flex justify-content-between align-items-center">
        {/* LEFT */}
        <div className="d-flex align-items-center flex-grow-1 min-w-0 me-2 me-lg-3">
          {isMobile && (
            <Dropdown
              isOpen={logoDropdown}
              toggle={() => setLogoDropdown(!logoDropdown)}
              className="navbar-brand-box"
            >
              <DropdownToggle tag="div" className="cursor-pointer">
                <img src={logo} alt="logo" height="22" />
              </DropdownToggle>

              <DropdownMenu>
                <button
                  className="dropdown-item text-danger"
                  onClick={handleLogout}
                >
                  <i className="fa fa-sign-out" />
                  Logout
                </button>
                <button
                  className="dropdown-item text-danger"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  <i className="fa fa-refresh" />
                  Hard Refresh
                </button>
                <div className="dropdown-divider" />
                <button
                  className={`dropdown-item ${
                    menuLayoutMode === "vertical" ? "active" : ""
                  }`}
                  onClick={() =>
                    typeof onMenuLayoutChange === "function" &&
                    onMenuLayoutChange("vertical")
                  }
                >
                  <i className="fa fa-columns me-1" />
                  Vertical Menu
                </button>
                <button
                  className={`dropdown-item ${
                    menuLayoutMode === "horizontal" ? "active" : ""
                  }`}
                  onClick={() =>
                    typeof onMenuLayoutChange === "function" &&
                    onMenuLayoutChange("horizontal")
                  }
                >
                  <i className="fa fa-window-maximize me-1" />
                  Horizontal Menu
                </button>
              </DropdownMenu>
            </Dropdown>
          )}

          {menuLayoutMode === "vertical" ? (
            <button
              type="button"
              onClick={handleSidebarToggle}
              className="btn btn-sm px-3 font-size-16 header-item"
              aria-label="Toggle sidebar"
              title="Hide sidebar for full-width content, or show it again"
            >
              <i className="fa fa-bars" />
            </button>
          ) : null}

          <HeaderGlobalSearch className="ms-2 ms-lg-3 flex-grow-1" />
        </div>

        {renderHeaderActions()}
      </div>
    </header>
  );
};

Header.propTypes = {
  toggleLeftmenu: PropTypes.func,
  toggleMenuCallback: PropTypes.func,
  changeSidebarType: PropTypes.func,
  leftSideBarType: PropTypes.string,
  showRightSidebar: PropTypes.bool,
  showRightSidebarAction: PropTypes.func,
  menuLayoutMode: PropTypes.oneOf(["vertical", "horizontal"]),
  onMenuLayoutChange: PropTypes.func,
};

const mapStateToProps = (state) => ({
  leftSideBarType: state.Layout?.leftSideBarType,
  showRightSidebar: state.Layout?.showRightSidebar,
});

export default connect(mapStateToProps, {
  showRightSidebarAction,
  toggleLeftmenu,
  changeSidebarType,
})(withTranslation()(Header));
